import { t } from 'javascripts/utils/withTranslation';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import { createFocusTrap } from 'focus-trap';
import h from 'hyperscript';

export default class StickyFlyout {
  constructor($flyout, config) {
    this.config = {
      modifierClassName: 'sticky-flyout',
      contentSelector: '.sticky-flyout__content',
      toggleSelector: '.sticky-flyout__toggle',
      toggleInnerSelector: '.sticky-flyout__toggle-text-inner',
      showOnScroll: true,
      closeText: t('Schließen'),
      openText: t('Info'),
      disableBodyScroll: false,
      ...config,
    };
    this.$flyout = $flyout;
    this.$flyoutContent = $flyout.querySelector(this.config.contentSelector);
    this.$toggleButton = $flyout.querySelector(this.config.toggleSelector);
    this.$inner = $flyout.querySelector(this.config.toggleInnerSelector);
    this.scrollTarget = document.querySelector('.page__content');

    this.init();

    // Binded events
    this.bindedOnOverlayClose = this.onOverlayClose.bind(this);
    this.bindedOnKeypress = this.onKeypress.bind(this);
    this.onOutsideClickBinded = this.onOutsideClick.bind(this);
  }

  init() {
    if (!this.$toggleButton) {
      console.warn(
        'Sticky Flyout toggle button not found. Node:',
        this.$flyout
      );
      return;
    }
    document.addEventListener('overlayClose', this.bindedOnOverlayClose);
    this.$toggleButton.addEventListener('click', this.toggleFlyout);
    const focusTrapTarget = this.$flyout;

    this.focusTrap = createFocusTrap(focusTrapTarget, {
      escapeDeactivates: true,
      clickOutsideDeactivates: true,
      returnFocusOnDeactivate: false,
    });

    if (this.scrollTarget && this.config.showOnScroll) {
      this.showToggleOnScroll();
      window.addEventListener('scroll', this.showToggleOnScroll);
    } else {
      this.$flyout.classList.add(
        `${this.config.modifierClassName}--visible-onscroll`
      );
    }

    this.$flyoutContent.setAttribute('aria-hidden', 'true');
    this.$flyoutContent.setAttribute('tabindex', '-1');
  }

  toggleFlyout = () => {
    if (this.$toggleButton) {
      const isExpanded =
        this.$toggleButton.getAttribute('aria-expanded') === 'true';

      this.$toggleButton.setAttribute(
        'aria-expanded',
        isExpanded ? 'false' : 'true'
      );
      if (isExpanded) {
        this.$flyout.classList.remove(
          `${this.config.modifierClassName}--expanded`
        );
      } else {
        this.$flyout.classList.add(
          `${this.config.modifierClassName}--expanded`
        );
      }
      this.$flyoutContent.setAttribute(
        'aria-hidden',
        isExpanded ? 'true' : 'false'
      );
      this.$flyoutContent.setAttribute('tabindex', isExpanded ? '-1' : '0');

      if (!isExpanded) {
        this.$flyout.focus();
        this.focusTrap.activate();

        this.$inner.textContent = this.config.closeText;

        document.addEventListener('keydown', this.bindedOnKeypress);
        document.addEventListener('click', this.onOutsideClickBinded);

        if (this.config.disableBodyScroll) {
          disableBodyScroll(this.$flyoutContent);
        }

        this.bodyOverlay = h(`div.${this.config.modifierClassName}__overlay`);
        this.$flyout.parentElement.appendChild(this.bodyOverlay);
      } else {
        this.$toggleButton.focus();
        if (this.focusTrap) {
          this.focusTrap.deactivate();
        }

        this.$inner.textContent = this.config.openText;

        document.removeEventListener('keydown', this.bindedOnKeypress);
        document.removeEventListener('click', this.onOutsideClickBinded);

        if (this.config.disableBodyScroll) {
          enableBodyScroll(this.$flyoutContent);
        }

        if (this.bodyOverlay) {
          this.$flyout.parentElement.removeChild(this.bodyOverlay);
        }
      }
    }
  };

  // Helper to get element's offset from top of document
  getOffsetTop = (el) => {
    let offsetTop = 0;
    let node = el;
    while (node) {
      offsetTop += node.offsetTop;
      node = node.offsetParent;
    }
    return offsetTop;
  };

  showToggleOnScroll = () => {
    const target = this.scrollTarget;
    if (!target) return;
    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;
    const top = this.getOffsetTop(target);
    const bottom = top + target.offsetHeight - (this.$flyout.offsetHeight + 50);
    const insideTarget = scrollTop >= top;
    const isBottom = scrollTop >= bottom;

    if (insideTarget && !isBottom) {
      this.$flyout.classList.add(
        `${this.config.modifierClassName}--scrolled-in`
      );
    } else {
      this.$flyout.classList.remove(
        `${this.config.modifierClassName}--scrolled-in`
      );
    }
  };

  onKeypress(event) {
    const isExpanded =
      this.$toggleButton.getAttribute('aria-expanded') === 'true';

    // Close on esc-key
    if (isExpanded && event.keyCode === 27) {
      window.requestAnimationFrame(() => {
        this.toggleFlyout();
      });
    }
  }

  onOutsideClick(event) {
    const $target = event.target;

    if ($target) {
      const isInside =
        $target === this.$flyout || this.$flyout.contains($target);

      if (!isInside) {
        this.onOverlayClose();
      }
    }
  }

  onOverlayClose() {
    // Close menu if event to close all overlays is fired
    window.requestAnimationFrame(() => this.toggleFlyout());
  }
}
document.querySelectorAll('.sticky-flyout').forEach(($flyout) => {
  const $toggle = $flyout?.querySelector('.sticky-flyout__toggle');
  const { closeText, openText } = $toggle?.dataset || {};

  return new StickyFlyout($flyout, {
    contentSelector: '.sticky-flyout__content',
    toggleSelector: '.sticky-flyout__toggle',
    toggleInnerSelector: '.sticky-flyout__toggle-text-inner',
    closeText,
    openText,
  });
});
